"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Facebook, Linkedin, Link2, Check } from "lucide-react";

/* ============================================================
   PATH IMPOR DIHITUNG DARI app/edukasi-kesehatan/, BUKAN DARI
   app/edukasi-kesehatan/[slug]/.

   Berkas ini memang DIRENDER oleh [slug]/page.jsx, tapi letaknya
   sendiri satu tingkat di atasnya. Path relatif selalu dihitung dari
   posisi berkas yang menuliskannya, bukan dari berkas yang
   memanggilnya - itu yang sempat saya keliru, dan hasilnya
   "Can't resolve '../../components/Footer'".

     ./          -> app/edukasi-kesehatan
     ../         -> app
     ../components, ../i18n   <- BENAR
     ./artikel, ./data        <- BENAR (tetangga sendiri)

   Kalau nanti berkas ini dipindah ke dalam [slug]/, semua path di
   bawah harus ditambah satu tingkat lagi. */
import Footer from "../../components/Footer";
import { useLanguage } from "../../i18n/LanguageProvider";
import { ARTIKEL_TERBARU } from "./artikel";
import { MATERI } from "./data";

/* ============================================================
   HALAMAN ARTIKEL - /edukasi-kesehatan/<slug>

   Susunannya mengikuti anatomi artikel kesehatan yang sudah baku:
   remah roti, label, judul, penulis, tanggal, tombol bagikan,
   ringkasan tebal, gambar, isi, FAQ, referensi, artikel terkait.
   Bukan karena rumah sakit lain memakainya, tapi karena urutan itu
   menjawab pertanyaan pembaca sesuai urutan munculnya: ini apa, siapa
   yang menulis, kapan, apa intinya, lalu detailnya.

   ============================================================
   LEBAR BACA DIKUNCI ~72 KARAKTER

   Baris teks yang terlalu panjang membuat mata kehilangan awal baris
   berikutnya saat berpindah. Untuk artikel yang isinya penjelasan
   medis - dan sering dibaca orang yang sedang cemas - itu bukan soal
   estetika. maxWidth ditulis inline, bukan lewat kelas prose atau
   max-w-*, karena kelas Tailwind di folder ini sudah berkali-kali
   gagal ter-generate.

   ============================================================
   SEMUA JARAK DAN UKURAN KRITIS DITULIS INLINE

   Alasan yang sama seperti di EdukasiContent.jsx. Yang boleh memakai
   kelas Tailwind hanya hal yang kalau hilang tidak merusak apa pun -
   warna teks, tebal huruf, transisi.
   ============================================================ */

const LEBAR_BACA = 720;

/* Dipakai HANYA saat kedua cara menyalin gagal. Ditulis sebagai
   objek { id, en } dan bukan kunci kamus karena cuma satu kalimat
   yang dipakai di satu tempat - pola yang sama dengan IgdContent
   dan MCUContent. */
const TEKS_SALIN_MANUAL = {
  id: "Salin tautan ini:",
  en: "Copy this link:",
};

function pilihTeks(nilai, lang) {
  if (nilai == null) return "";
  if (typeof nilai === "string") return nilai;
  return nilai[lang] || nilai.id || "";
}

/* Tanggal diformat mengikuti bahasa, dengan zona waktu dikunci ke
   Asia/Jakarta. Tanpa penguncian itu, peladen (biasanya UTC) dan
   browser bisa menghasilkan tanggal berbeda dan React melaporkannya
   sebagai hydration mismatch. */
function formatTanggal(tanggal, lang) {
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  }).format(new Date(tanggal));
}

export default function ArtikelContent({ artikel }) {
  const { lang, t } = useLanguage();
  const [tersalin, setTersalin] = useState(false);

  /* ============================================================
     DUA KOLOM DIATUR DARI JAVASCRIPT, BUKAN BREAKPOINT CSS.

     Kelas Tailwind di folder ini sudah berkali-kali gagal
     ter-generate, dan untuk tata letak dua kolom kegagalannya tidak
     halus: seluruh sidebar akan menumpuk di atas isi artikel tanpa
     pesan apa pun. Lebar layar diukur langsung, lalu dipakai sebagai
     style inline.

     Ambangnya 1024px. Di bawah itu sidebar turun ke bawah artikel -
     kolom selebar 300px di samping teks pada layar 800px akan membuat
     baris bacaan tinggal 40 karakter, terlalu sempit untuk dibaca
     nyaman.

     ⚠️ Render pertama selalu satu kolom, karena peladen tidak tahu
     lebar layar pengunjung. Di layar lebar akan terlihat pergeseran
     sepersekian detik saat sidebar berpindah ke kanan. Itu harga dari
     mengukur di browser; menghindarinya menuntut media query CSS,
     yang justru tidak bisa diandalkan di berkas ini.
     ============================================================ */
  const [duaKolom, setDuaKolom] = useState(false);

  /* Alamat halaman baru bisa dibaca setelah komponen menempel di
     browser - di peladen tidak ada `window`. Selama masih kosong,
     tombol bagikan dirender nonaktif alih-alih menunjuk ke alamat
     yang salah. */
  const [alamat, setAlamat] = useState("");
  useEffect(() => setAlamat(window.location.href), []);

  useEffect(() => {
    const ukur = () => setDuaKolom(window.innerWidth >= 1024);
    ukur();
    window.addEventListener("resize", ukur);
    return () => window.removeEventListener("resize", ukur);
  }, []);

  const judul = pilihTeks(artikel.judul, lang);
  const peninjau = pilihTeks(artikel.ditinjau, lang);
  const materi = artikel.materi
    ? MATERI.find((m) => m.id === artikel.materi)
    : null;

  const terkait = ARTIKEL_TERBARU.filter((a) => a.slug !== artikel.slug).slice(0, 3);

  /* ============================================================
     SALIN TAUTAN - TIGA LAPIS, KARENA SATU TIDAK CUKUP

     navigator.clipboard HANYA ada di secure context: HTTPS atau
     localhost. Situs yang dibuka lewat alamat LAN - misalnya
     http://192.168.0.109:3005 saat menguji dari HP - TIDAK
     termasuk, dan di sana navigator.clipboard bernilai undefined.

     Versi sebelumnya mendiamkan kegagalan itu. Akibatnya tombolnya
     terlihat rusak padahal cuma tidak didukung: diklik, tidak ada
     yang berubah, dan tidak ada satu pun petunjuk kenapa.

     Sekarang berurutan:
       1. navigator.clipboard - jalur normal di produksi (HTTPS)
       2. execCommand("copy") - usang, tapi satu-satunya yang
          bekerja di halaman non-HTTPS
       3. window.prompt berisi tautan tersorot - tinggal Ctrl+C

     Lapis 3 tidak elegan, dan memang tidak dimaksudkan elegan. Ia
     ada supaya tombol ini TIDAK PERNAH diam.
     ============================================================ */
  const salinTautan = async () => {
    const url = window.location.href;

    const tandaiBerhasil = () => {
      setTersalin(true);
      setTimeout(() => setTersalin(false), 2000);
    };

    try {
      /* Cek window.isSecureContext, BUKAN cuma keberadaan
         navigator.clipboard. Sebagian peramban tetap memunculkan
         objeknya di halaman non-HTTPS tapi menolak saat dipanggil,
         jadi pemeriksaan keberadaan saja tidak cukup. */
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        salinCaraLama(url);
      }
      tandaiBerhasil();
    } catch {
      try {
        salinCaraLama(url);
        tandaiBerhasil();
      } catch {
        window.prompt(pilihTeks(TEKS_SALIN_MANUAL, lang), url);
      }
    }
  };

  return (
    <div className="w-full bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">

      {/* ---------- BREADCRUMB ---------- */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div
          style={{ maxWidth: 1600, margin: "0 auto", padding: "12px 16px" }}
        >
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("header.nav.home")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                <Link href="/edukasi-kesehatan/artikel" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline">
                  {t("artikelDetail.breadcrumb")}
                </Link>
                <Pemisah />
              </li>
              <li className="flex items-center">
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {judul}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div
        style={{
          maxWidth: 1140,
          margin: "0 auto",
          padding: "40px 16px 56px",
          display: "grid",
          gridTemplateColumns: duaKolom ? "minmax(0, 1fr) 300px" : "minmax(0, 1fr)",
          gap: 48,
          alignItems: "start",
        }}
      >

      <article style={{ maxWidth: LEBAR_BACA, minWidth: 0 }}>
        {/* ---------- SPANDUK BELUM DITINJAU: DIHAPUS ----------
           Di sini dulu ada spanduk kuning yang muncul saat `ditinjau`
           di artikel.js masih kosong. Spanduk itu dihapus atas permintaan.

           Yang perlu diketahui siapa pun yang membaca ini nanti:
           penghapusannya TIDAK mengisi `ditinjau`. Keempat artikel yang
           ada sekarang masih ditinjau: { id: "", en: "" }, dan komentar
           di artikel.js sendiri menyebut kolom itu wajib diisi sebelum
           tayang. Bedanya hanya satu: sekarang keadaan itu tidak lagi
           terlihat di halaman, jadi tidak ada yang mengingatkan.

           Isi `ditinjau` dengan nama dokter peninjau begitu tersedia.
           Baris "Ditinjau oleh..." di bawah nama penulis akan muncul
           sendiri - bagian itu masih utuh dan tidak ikut dihapus. */}

        <span
          style={{
            display: "inline-block",
            borderRadius: 8,
            backgroundColor: "#0f6a3b",
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 700,
            color: "#ffffff",
            letterSpacing: "0.04em",
          }}
        >
          {t("artikelDetail.label")}
        </span>

        <h1
          className="text-balance font-bold leading-[1.15] tracking-tight text-[var(--c-ink)]"
          style={{ marginTop: 18, fontSize: 30 }}
        >
          {judul}
        </h1>

        {/* ---------- BARIS PENULIS ---------- */}
        <div style={{ marginTop: 18 }} className="text-[13px] text-[var(--c-muted)] sm:text-sm">
          <p>{t("artikelDetail.oleh", { penulis: pilihTeks(artikel.penulis, lang) })}</p>

          {peninjau && (
            <p style={{ marginTop: 2 }} className="font-semibold text-[var(--c-brand)]">
              {t("artikelDetail.ditinjau", { peninjau })}
            </p>
          )}

          <div
            style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}
          >
            <time dateTime={artikel.tanggal}>{formatTanggal(artikel.tanggal, lang)}</time>

            <span aria-hidden="true" style={{ width: 1, height: 16, backgroundColor: "#cbd5e1" }} />

            {/* ---------- TOMBOL BAGIKAN ----------
               Empat tombol: Facebook, X, LinkedIn, dan salin tautan.

               Ini tombol BERBAGI, bukan tautan ke akun RS. Bedanya
               penting: ikon yang sama di footer membawa pengunjung ke
               akun rumah sakit, sedangkan di sini ia membuka jendela
               berbagi berisi artikel yang sedang dibaca. Karena itu
               alamatnya dibangun dari window.location, bukan dari
               daftar akun di data/contact.js.

               encodeURIComponent wajib: tanpa itu, judul yang
               mengandung tanda tanya - dan judul artikel ini memang
               mengandungnya - akan memotong parameter di URL berbagi. */}
            <ul style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <li>
                <TombolBagikan
                  Ikon={Facebook}
                  nama="Facebook"
                  href={alamat ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(alamat)}` : ""}
                  t={t}
                />
              </li>
              <li>
                <TombolBagikan
                  Ikon={Linkedin}
                  nama="LinkedIn"
                  href={alamat ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(alamat)}` : ""}
                  t={t}
                />
              </li>
              <li>
                <button
                  type="button"
                  onClick={salinTautan}
                  aria-label={tersalin ? t("artikelDetail.tautanTersalin") : t("artikelDetail.salinTautan")}
                  title={tersalin ? t("artikelDetail.tautanTersalin") : t("artikelDetail.salinTautan")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    color: tersalin ? "#15803d" : "#0f6a3b",
                  }}
                  className="transition-colors hover:bg-[var(--c-tint)]"
                >
                  {tersalin ? <Check size={17} /> : <Link2 size={17} />}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* ---------- RINGKASAN ----------
           Dicetak lebih besar dan tebal. Ini kalimat yang menentukan
           apakah orang membaca terus atau pergi, jadi ia tidak boleh
           terlihat sama dengan paragraf biasa. */}
        <p
          className="font-semibold text-[var(--c-ink)]"
          style={{ marginTop: 26, fontSize: 17, lineHeight: 1.6 }}
        >
          {pilihTeks(artikel.ringkas, lang)}
        </p>

        {artikel.gambar && (
          <div
            style={{
              position: "relative",
              marginTop: 26,
              aspectRatio: "3 / 2",
              width: "100%",
              overflow: "hidden",
              borderRadius: 12,
              backgroundColor: "#f1faf5",
            }}
          >
            <Image
              src={artikel.gambar}
              alt={pilihTeks(artikel.gambarAlt, lang)}
              fill
              sizes="(min-width: 768px) 720px, 100vw"
              priority
              style={{ objectFit: "cover", objectPosition: "top" }}
            />
          </div>
        )}

        {/* ---------- ISI ---------- */}
        <div style={{ marginTop: 30 }}>
          {artikel.isi.map((blok, i) => (
            <Blok key={i} blok={blok} lang={lang} t={t} />
          ))}
        </div>

        {/* ---------- MATERI CETAK TERKAIT ---------- */}
        {materi && (
          <div
            style={{
              marginTop: 40,
              borderRadius: 12,
              backgroundColor: "#f1faf5",
              border: "1px solid #bfe3cf",
              padding: "16px 18px",
            }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)]">
              {t("artikelDetail.sumberMateri")}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-[var(--c-body)] sm:text-sm">
              {t("artikelDetail.sumberMateriTeks")}
            </p>
            <Link
              href="/edukasi-kesehatan"
              className="mt-3 inline-flex items-center gap-2 text-[13px] font-bold text-[var(--c-brand)] underline-offset-4 hover:underline sm:text-sm"
            >
              {pilihTeks(materi.judul, lang)}
              <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        )}

        {/* ---------- FAQ ----------
           Ditulis sebagai pasangan judul-paragraf biasa, bukan
           akordeon. Jawaban yang tersembunyi di balik klik tidak ikut
           terbaca saat orang memindai halaman, dan pada pertanyaan
           kesehatan justru bagian itulah yang dicari. */}
        {artikel.faq?.length > 0 && (
          <section style={{ marginTop: 44 }}>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ fontSize: 22 }}>
              {t("artikelDetail.faq")}
            </h2>

            {artikel.faq.map((item, i) => (
              <div key={i} style={{ marginTop: 22 }}>
                <h3 className="font-bold text-[var(--c-ink)]" style={{ fontSize: 16 }}>
                  {pilihTeks(item.tanya, lang)}
                </h3>
                <p
                  className="text-slate-700"
                  style={{ marginTop: 8, fontSize: 15, lineHeight: 1.75 }}
                >
                  {pilihTeks(item.jawab, lang)}
                </p>
              </div>
            ))}
          </section>
        )}

        {/* ---------- REFERENSI ----------
           Tidak dirender sama sekali kalau kosong. Judul "Referensi"
           dengan daftar kosong di bawahnya lebih buruk daripada tidak
           ada bagian referensi: ia menjanjikan sesuatu yang tidak ada. */}
        {artikel.referensi?.length > 0 && (
          <section style={{ marginTop: 44 }}>
            <h2 className="font-bold text-[var(--c-ink)]" style={{ fontSize: 18 }}>
              {t("artikelDetail.referensi")}
            </h2>

            <ol style={{ marginTop: 14, paddingLeft: 20, listStyle: "decimal" }}>
              {artikel.referensi.map((ref, i) => (
                <li
                  key={i}
                  className="text-[var(--c-body)]"
                  style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7 }}
                >
                  {ref.teks}{" "}
                  {ref.url && (
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-all text-[var(--c-brand)] underline underline-offset-2"
                    >
                      {ref.url}
                    </a>
                  )}
                  {ref.diakses && (
                    <span> &middot; {t("artikelDetail.diakses", { tanggal: ref.diakses })}</span>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* ---------- CATATAN ---------- */}
        <p
          className="text-[var(--c-body)]"
          style={{
            marginTop: 44,
            borderRadius: 12,
            backgroundColor: "#f1faf5",
            border: "1px solid #bfe3cf",
            padding: "16px 18px",
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          {t("artikelDetail.catatan")}
        </p>

        <Link
          href="/edukasi-kesehatan/artikel"
          className="inline-flex items-center gap-2 font-bold text-[var(--c-brand)] underline-offset-4 hover:underline"
          style={{ marginTop: 40, fontSize: 14 }}
        >
          <span aria-hidden="true">&larr;</span>
          {t("artikelDetail.kembali")}
        </Link>
      </article>

      {/* ---------- SIDEBAR: ARTIKEL TERKAIT ----------
         sticky supaya tetap terlihat saat artikel panjang digulir.
         top 132 = tinggi header 112 + jarak 20. Kalau tinggi header
         berubah, ubah angka ini juga.

         Di layar sempit sidebar ini turun ke bawah artikel dan sticky
         dimatikan - elemen yang menempel di layar kecil justru
         memakan ruang baca yang sudah terbatas. */}
      <aside
        style={
          duaKolom
            ? { position: "sticky", top: 132, minWidth: 0 }
            : { minWidth: 0 }
        }
      >
        {terkait.length > 0 && (
          <section>
            <h2
              className="font-bold text-[var(--c-ink)]"
              style={{
                fontSize: 16,
                paddingBottom: 10,
                borderBottom: "2px solid #0f6a3b",
              }}
            >
              {t("artikelDetail.terkait")}
            </h2>

            <ul>
              {terkait.map((a) => (
                <li key={a.slug} style={{ borderBottom: "1px solid #e2e8f0", padding: "14px 0" }}>
                  <p className="text-[12px] text-[var(--c-muted)]">
                    {formatTanggal(a.tanggal, lang)}
                  </p>
                  <Link
                    href={`/edukasi-kesehatan/${a.slug}`}
                    className="mt-1 block font-bold leading-snug text-[var(--c-ink)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline"
                    style={{ fontSize: 14.5 }}
                  >
                    {pilihTeks(a.judul, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </aside>

      </div>

      <Footer />
    </div>
  );
}

/* ============================================================
   TOMBOL BAGIKAN

   Dibuat sebagai <a>, bukan <button>: tujuannya memang berpindah ke
   alamat lain, dan hanya <a> yang bisa dibuka di tab baru lewat klik
   tengah atau Ctrl+klik - kebiasaan yang dipakai banyak orang tanpa
   berpikir.

   rel="noopener noreferrer" wajib untuk target="_blank": tanpa itu,
   halaman tujuan bisa mengakses window.opener dan mengarahkan ulang
   tab kita.
   ============================================================ */

function TombolBagikan({ Ikon, nama, href, t }) {
  if (!href) {
    return (
      <span
        aria-hidden="true"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 34,
          height: 34,
          borderRadius: 8,
          color: "#cbd5e1",
        }}
      >
        <Ikon size={17} />
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("artikelDetail.bagikanKe", { jaringan: nama })}
      title={t("artikelDetail.bagikanKe", { jaringan: nama })}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: 8,
        color: "#0f6a3b",
      }}
      className="transition-colors hover:bg-[var(--c-tint)]"
    >
      <Ikon size={17} />
    </a>
  );
}

/* ============================================================
   SALIN CARA LAMA - document.execCommand("copy")

   Sudah ditandai usang, dan tetap dipakai dengan sadar: ia
   satu-satunya cara menyalin di halaman non-HTTPS, dan masih
   didukung seluruh peramban arus utama.

   Textarea-nya WAJIB menempel di dokumen sebelum bisa disorot -
   elemen lepas tidak bisa di-select(). position fixed + opacity 0
   supaya halaman tidak melompat saat elemen itu ada sepersekian
   detik, dan readonly supaya papan ketik virtual di ponsel tidak
   ikut naik.

   Melempar error kalau gagal, BUKAN mengembalikan false - itu yang
   membuat blok catch di salinTautan bisa turun ke lapis prompt.
   ============================================================ */
function salinCaraLama(teks) {
  const ta = document.createElement("textarea");
  ta.value = teks;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  ta.setSelectionRange(0, teks.length);
  const berhasil = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!berhasil) throw new Error("execCommand copy gagal");
}

function Pemisah() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ============================================================
   BLOK ISI

   Satu fungsi, satu cabang per jenis blok. Jenis yang tidak dikenali
   mengembalikan null alih-alih melempar error - artikel dengan satu
   blok salah ketik tetap tampil, hanya kehilangan blok itu.
   ============================================================ */

function Blok({ blok, lang, t }) {
  if (blok.tipe === "judul") {
    return (
      <h2
        className="font-bold text-[var(--c-ink)]"
        style={{ marginTop: 34, marginBottom: 12, fontSize: 21, lineHeight: 1.3 }}
      >
        {pilihTeks(blok.teks, lang)}
      </h2>
    );
  }

  if (blok.tipe === "paragraf") {
    return (
      <p
        className="text-slate-700"
        style={{ marginTop: 16, fontSize: 15.5, lineHeight: 1.8 }}
      >
        {pilihTeks(blok.teks, lang)}
      </p>
    );
  }

  if (blok.tipe === "daftar" || blok.tipe === "daftarAngka") {
    const bernomor = blok.tipe === "daftarAngka";
    const Tag = bernomor ? "ol" : "ul";

    return (
      <Tag
        style={{
          marginTop: 16,
          paddingLeft: 22,
          listStyle: bernomor ? "decimal" : "disc",
        }}
      >
        {blok.butir.map((b, i) => (
          <li
            key={i}
            className="text-slate-700 marker:font-bold marker:text-[var(--c-brand-mid)]"
            style={{ marginTop: 8, paddingLeft: 4, fontSize: 15.5, lineHeight: 1.75 }}
          >
            {pilihTeks(b, lang)}
          </li>
        ))}
      </Tag>
    );
  }

  if (blok.tipe === "bacaJuga") {
    return (
      <p style={{ marginTop: 20, fontSize: 15 }}>
        <span className="font-bold text-[var(--c-ink)]">{t("artikelDetail.bacaJuga")} </span>
        <Link
          href={blok.href}
          className="font-semibold text-[var(--c-brand)] underline underline-offset-4"
        >
          {pilihTeks(blok.teks, lang)}
        </Link>
      </p>
    );
  }

  return null;
}
